import { Component, Input, OnInit } from '@angular/core';
import { Guid } from 'guid-typescript';
import { combineLatest, Observable } from 'rxjs';
import { createProduct, Product, TotalCostsService } from '../shared';
import { ChargingStation, ChargingStationEntry, CsSetupInfo, CsSetupSection, ElectricityMeterUnitEntry, EmuEntryInfo } from './charging-stations-section.model';
import { ChargingStationsService } from './services/charging-stations.service';
import { ChargingStationsQuery } from './state/charging-stations.state';
import { CsSetupSectionsService } from './services/cs-setup-sections.service';
import { EmuEntryQuery } from './state/emu-entry.state';
import { EmuEntryService } from './services/emu-entry.service';


@Component({
  selector: 'app-charging-stations-section',
  templateUrl: './charging-stations-section.component.html',
  styleUrls: ['./charging-stations-section.component.scss']
})
export class ChargingStationsSectionComponent implements OnInit {
  @Input() projectId: Guid;
  chargingStations: ChargingStation[];
  csSetupInfos: CsSetupInfo[] = [];
  electricityMeterUnitInfo: EmuEntryInfo;
  internalPossibleElectricityMeterUnits: Product[] = [];
  private internalIsBusy: boolean;
  private readonly csSetupSectionsWhichMarkedEmuForDeletion: Guid[] = [];

  public get isBusy(): boolean {
    return this.internalIsBusy;
  }

  get addElectricityMeterUnitPossible(): boolean {
    return !this.hasElectricityMeterUnitEntry &&
            this.possibleElectricityMeterUnits.length > 0;
  }

  get hasElectricityMeterUnitEntry(): boolean {
    return !!this.electricityMeterUnitInfo;
  }

  get possibleElectricityMeterUnits(): Product[] {
    return this.internalPossibleElectricityMeterUnits;
  }

  get hasCsSetups(): boolean {
    return this.csSetupInfos.length > 0;
  }

  get emuIsMarkedForDeletion(): boolean {
    return this.csSetupSectionsWhichMarkedEmuForDeletion.length > 0;
  }

  constructor(
    private chargingStationsService: ChargingStationsService,
    private chargingStationsQuery: ChargingStationsQuery,
    private csSetupSectionsService: CsSetupSectionsService,
    private emuEntryQuery: EmuEntryQuery,
    private emuEntryService: EmuEntryService,
    private totalCostsService: TotalCostsService)
  {
    combineLatest([this.chargingStationsService.isBusy$, this.csSetupSectionsService.isBusy$, this.emuEntryService.isBusy$])
      .subscribe(([ib1, ib2, ib3]) => this.internalIsBusy = ib1 || ib2 || ib3);
  }

  ngOnInit(): void {
    this.loadInitialData()
        .subscribe(([chargingStations, csSetupSections, emuEntry]) => {
          this.chargingStations = chargingStations;
          this.csSetupInfos = this.createCsSetupInfos(this.projectId, csSetupSections);
          this.electricityMeterUnitInfo = this.createElectricityMeterUnitInfo(this.projectId, emuEntry);
          this.internalPossibleElectricityMeterUnits = this.getPossibleElectricityMeterUnits(this.csSetupInfos);
          this.totalCostsService.sectionInitialized('csSetup');
        });
  }

  markEmuForDeletion(csSetupSectionId: Guid): void {
    this.csSetupSectionsWhichMarkedEmuForDeletion.push(csSetupSectionId);
  }

  unmarkEmuForDeletion(csSetupSectionId: Guid): void {
    this.removeCsSetupSectionsFromMarkedForDeletionArray(csSetupSectionId);
  }

  addNewCsSetupSection(): void {
    const newSection = createNewCsSetupSection(Guid.create(), new Date(), this.chargingStations[0], 1);
    this.csSetupInfos.push({projectId: this.projectId, csSetupSection: newSection, isNew: true});
    this.gotoLastCsSetup();
  }

  removeCsSetupSection(csSetupSectionId: Guid): void {
    const csSetupInfo = this.csSetupInfos.find(info => info.csSetupSection.id.equals(csSetupSectionId));
    const index = this.csSetupInfos.indexOf(csSetupInfo);
    if (index < 0) {
      return;
    }
    this.csSetupInfos.splice(index, 1);

    this.updatePossibleElectricityMeterUnits();

    // Check if the affected CsSetup is new to avoid unexpected deletion of the EMU
    // if there are any product updates which makes the current EMU invalid.
    if (!csSetupInfo.isNew) {
      this.removeCurrentElectricityMeterUnitIfItIsInvalid();
    }
  }

  onUpdatedCsSetupSection(csSetupSection: CsSetupSection): void {
    const csSetupInfo = this.csSetupInfos.find(info => info.csSetupSection.id.equals(csSetupSection.id));
    const csSetupInfoIsNew = csSetupInfo.isNew;
    csSetupInfo.csSetupSection = csSetupSection;
    csSetupInfo.isNew = false;

    this.updatePossibleElectricityMeterUnits();

    // Check if the affected CsSetup is new to avoid unexpected deletion of the EMU
    // if there are any product updates which makes the current EMU invalid.
    if (!csSetupInfoIsNew) {
      this.removeCurrentElectricityMeterUnitIfItIsInvalid();
    }
  }

  addNewElectricityMeterUnit(): void {
    this.electricityMeterUnitInfo = {
      projectId: this.projectId,
      emuEntry: createNewEmuEntry(this.possibleElectricityMeterUnits[0]),
      isNew: true
    };
    this.gotoLastCsSetup();
  }

  removeElectricityMeterUnit(): void {
    if (this.electricityMeterUnitInfo.isNew) {
      this.emuEntryService.updateOnlyInStore(undefined);
      this.electricityMeterUnitInfo = undefined;
    } else {
      this.emuEntryService.remove(this.electricityMeterUnitInfo.projectId).subscribe(
        emuEntry => {
          this.electricityMeterUnitInfo = undefined;
          this.csSetupSectionsWhichMarkedEmuForDeletion.splice(0, this.csSetupSectionsWhichMarkedEmuForDeletion.length);
        },
        error => {});
    }
  }

  onUpdatedElectricityMeterUnit(emuEntry: ElectricityMeterUnitEntry): void {
    this.electricityMeterUnitInfo.isNew = false;
    this.electricityMeterUnitInfo.emuEntry = {...emuEntry};
  }

  private loadInitialData(): Observable<[ChargingStation[], CsSetupSection[], ElectricityMeterUnitEntry]> {
    const chargingStations$ = this.chargingStationsService.getChargingStations();
    const csSetupSections$ = this.csSetupSectionsService.getAll(this.projectId);
    const emuEntry$ = this.emuEntryService.getEmuEntry(this.projectId);
    return combineLatest([chargingStations$, csSetupSections$, emuEntry$]);
  }

  private createCsSetupInfos(projectId: Guid, csSetupSections: CsSetupSection[]): CsSetupInfo[] {
    return csSetupSections
      .map(section => ({projectId, csSetupSection: section, isNew: false}));
  }

  private removeCsSetupSectionsFromMarkedForDeletionArray(csSetupSectionId: Guid): void {
    const index = this.csSetupSectionsWhichMarkedEmuForDeletion.findIndex(siex => siex.equals(csSetupSectionId));
    if (index < 0) {
      return;
    }
    this.csSetupSectionsWhichMarkedEmuForDeletion.splice(index, 1);
  }

  private createElectricityMeterUnitInfo(projectId: Guid, emuEntry: ElectricityMeterUnitEntry): EmuEntryInfo {
    return emuEntry
      ? {projectId, emuEntry, isNew: false}
      : undefined;
  }

  private updatePossibleElectricityMeterUnits(): void {
    this.internalPossibleElectricityMeterUnits = this.getPossibleElectricityMeterUnits(this.csSetupInfos);
  }

  private getPossibleElectricityMeterUnits(csSetupInfos: CsSetupInfo[]): Product[] {
    return this.chargingStationsQuery.getPossibleElectricityMeterUnits(
      this.csSetupInfos.map(info => info.csSetupSection.chargingStationEntry.productId));
  }

  private removeCurrentElectricityMeterUnitIfItIsInvalid(): void {
    // No action needed if there is currently no electricity meter unit (emu) selected or the selected emu is valid.
    if (!this.hasElectricityMeterUnitEntry || this.emuEntryQuery.isCurrentEmuValid()) {
      return;
    }
    this.removeElectricityMeterUnit();
  }

  gotoLastCsSetup(): void {
    const element = document.getElementById('lastCsSetup');
    element.scrollIntoView({block: 'start', behavior: 'smooth'});
  }
}

function createNewEmuEntry(electricityMeterUnit: Product): ElectricityMeterUnitEntry {
  return {
    id: Guid.create(),
    productId: electricityMeterUnit.id,
    productVersion: electricityMeterUnit.version,
    description: electricityMeterUnit.name,
    pricePerUnit: electricityMeterUnit.pricePerUnit,
    createdOn: new Date(),
    discountFactor: 0,
    isAlternative: false,
    positionNumber: 1
  };
}

function createNewCsSetupSection(id: Guid, createdOn: Date, chargingStation: Product, quantity: number): CsSetupSection {
  const chargingStationEntry: ChargingStationEntry = {
    id: Guid.create(),
    sectionId: id,
    productId: chargingStation?.id,
    productVersion: chargingStation?.version,
    description: chargingStation?.name,
    pricePerUnit: chargingStation?.pricePerUnit,
    createdOn,
    quantity,
    discountFactor: 0,
    isAlternative: false,
    positionNumber: -1
  };
  return {
    id,
    createdOn,
    chargingStationEntry,
    productRelatedEntries: [],
    customEntries: []
  };
}
